/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { jsx, cssMap } from '@compiled/react';
import { PREMIUM_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import Link from '@atlaskit/link/link';
import Banner from '@atlaskit/banner/banner';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

const descriptionMessage1 = defineMessage({
	id: 'jira.platform.ui.limits-banner.description1',
	defaultMessage:
		'The scheme size limits we announced in August will be enforced starting in March. <link>Use the Site Optimizer</link> to reduce the size of your schemes.',
	description: 'Description message for limits banner when site optimizer is available',
});

const descriptionMessage2 = defineMessage({
	id: 'jira.platform.ui.limits-banner.description2',
	defaultMessage:
		'The scheme size limits we announced in August will be enforced starting in March. <link>More about streamlining schemes</link>',
	description: 'Description message for limits banner when site optimizer is not available',
});

const linkStyles = cssMap({
	content: {
		color: token('color.link'),
	},
});

export const LimitsBanner = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const appEditions = useAppEditions();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isPremium = appEditions?.software === PREMIUM_EDITION;

	const optimizeHref = fg('enable_cfo_dashboard')
		? '/jira/settings/system/performance/dashboard?tab=limitsAndCleanupPerformancePage'
		: '/jira/settings/system/performance/overview';

	const optimizeHrefFallback =
		'https://support.atlassian.com/jira-cloud-administration/docs/optimize-fields-in-your-project/';

	const handleLinkClick = () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'preEnforcementLimitsBannerLink',
				action: 'clicked',
			}),
		);
	};

	return (
		<Banner appearance="warning" icon={<WarningIcon label="" />}>
			{isPremium
				? formatMessage(descriptionMessage1, {
						link: (chunks: ReactNode) => (
							<span css={linkStyles.content}>
								<Link href={optimizeHref} target="_blank" onClick={handleLinkClick}>
									{chunks}
								</Link>
							</span>
						),
					})
				: formatMessage(descriptionMessage2, {
						link: (chunks: ReactNode) => (
							<span>
								<Link href={optimizeHrefFallback} target="_blank" onClick={handleLinkClick}>
									{chunks}
								</Link>
							</span>
						),
					})}
		</Banner>
	);
};

export default LimitsBanner;
